import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { ExtractionField, type ExtractionFieldData } from './ExtractionField';
import { deriveFieldStatus } from './ConfidenceBadge';
import { Check, ArrowRight, AlertCircle, FileText, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Slider } from '@/components/ui/slider';
import { computeBreakdownReadiness } from '@/lib/intake/computeBreakdownReadiness';
import type { UploadedFile } from './ContextIntakeScreen';

export interface ConsistencyWarning {
  type: string;
  message: string;
  fields: string[];
}

export interface ExtractionResult {
  fields: Record<string, ExtractionFieldData>;
  missing_fields: string[];
  conflicts: Array<{ field_name: string; reason: string; source_a?: string | null; source_b?: string | null }>;
  classification: {
    decision_type: string;
    decision_subtype: string;
    confidence: number;
    reasoning: string;
    suggested_title: string;
    classification_signals: string[];
    preliminary_impact_level: string;
    affected_roles: string[];
  };
  derived: {
    core_assumptions: string[];
    key_risks: string[];
  };
  consistency_warnings?: ConsistencyWarning[];
  model: string;
  extraction_version: number;
  pipeline_version?: string;
  audit_log_id?: string | null;
}

interface VisibleBreakdownScreenProps {
  extraction: ExtractionResult;
  onUpdateField: (key: string, value: string) => void;
  onConfirmDilemma: () => void;
  onUpdateScore: (key: 'impactScore' | 'urgencyScore', value: number) => void;
  onAcceptScore?: (key: 'impactScore' | 'urgencyScore') => void;
  onUpdatePreference: (pref: 'a' | 'b' | 'undecided') => void;
  onContinue: () => void;
  dilemmaConfirmed: boolean;
  uploadedFiles?: UploadedFile[];
}

const FIELD_LABELS: Record<string, string> = {
  optionA: 'Option A',
  optionB: 'Option B',
  decisionType: 'Entscheidungsbereich',
  impactScore: 'Impact-Score',
  urgencyScore: 'Dringlichkeit',
  initialPreference: 'Tendenz',
};

function defaultField(value: unknown, confidence = 0): ExtractionFieldData {
  return {
    value,
    confidence,
    source: 'llm',
    detection_status: value ? 'detected' : 'missing',
    confirmation_status: 'unreviewed',
    source_snippet: null,
  };
}

// ── Collapsible Section ──

const CollapsibleSection = ({
  title,
  defaultOpen = false,
  children,
  badge,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  badge?: React.ReactNode;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-border/30 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-muted/20 transition-colors"
      >
        <div className="flex items-center gap-3">
          {open
            ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground/40" />
            : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/40" />
          }
          <h3 className="text-[11px] font-semibold text-muted-foreground/60 uppercase tracking-[0.15em]">{title}</h3>
        </div>
        {badge}
      </button>
      {open && <div className="px-5 pb-5 space-y-4">{children}</div>}
    </div>
  );
};

// ── Conflict Notice ──

const ConflictNotice = ({ conflict }: { conflict: { field_name: string; reason: string; source_a?: string | null; source_b?: string | null } }) => (
  <div className="flex items-start gap-2.5 text-sm bg-amber-500/[0.04] border border-amber-500/15 rounded-lg px-4 py-3">
    <AlertCircle className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
    <div>
      <p className="text-amber-800 dark:text-amber-400 text-xs font-medium">{FIELD_LABELS[conflict.field_name] || conflict.field_name}: Widerspruch</p>
      <p className="text-muted-foreground text-xs mt-0.5">{conflict.reason}</p>
      {(conflict.source_a || conflict.source_b) && (
        <div className="flex gap-3 mt-1.5 text-[11px] text-muted-foreground/60">
          {conflict.source_a && <span>A: „{conflict.source_a}"</span>}
          {conflict.source_b && <span>B: „{conflict.source_b}"</span>}
        </div>
      )}
    </div>
  </div>
);

// ── Main Component ──

export const VisibleBreakdownScreen = ({
  extraction,
  onUpdateField,
  onConfirmDilemma,
  onUpdateScore,
  onAcceptScore,
  onUpdatePreference,
  onContinue,
  dilemmaConfirmed,
  uploadedFiles,
}: VisibleBreakdownScreenProps) => {
  const { fields, derived, classification, missing_fields, conflicts } = extraction;
  const preference = String(fields.initialPreference?.value || 'undecided') as 'a' | 'b' | 'undecided';
  const impactScore = Number(fields.impactScore?.value || 5);
  const urgencyScore = Number(fields.urgencyScore?.value || 5);

  const parsedFiles = (uploadedFiles || []).filter(f => f.status === 'uploaded' || f.status === 'parsed');
  const hasDocSources = parsedFiles.length > 0;

  const readiness = useMemo(() =>
    computeBreakdownReadiness(fields, missing_fields, conflicts),
    [fields, missing_fields, conflicts],
  );

  const canContinue = dilemmaConfirmed && readiness.canProceedToCanonicalization;

  const dilemmaConflicts = useMemo(() =>
    conflicts.filter(c => ['optionA', 'optionB', 'initialPreference'].includes(c.field_name)),
    [conflicts],
  );
  const classificationConflicts = useMemo(() =>
    conflicts.filter(c => ['decisionType', 'decisionSubtype', 'impactScore', 'urgencyScore'].includes(c.field_name)),
    [conflicts],
  );

  return (
    <div className="w-full flex flex-col items-center justify-start min-h-[60vh] px-4 py-8 animate-fade-in">
      <div className="w-full max-w-[720px] space-y-10">

        {/* ══════════════════════════════════════════════════════════ */}
        {/* ── HERO HEADER — direct, authoritative ──                */}
        {/* ══════════════════════════════════════════════════════════ */}
        <div className="space-y-3 text-center">
          <h2 className="text-[26px] md:text-[30px] font-serif font-semibold tracking-[-0.01em] text-foreground leading-[1.25]">
            Das ist der Kern deiner Entscheidung
          </h2>
          <p className="text-[13px] text-foreground/38 leading-relaxed max-w-md mx-auto">
            Wenn das nicht stimmt, korrigiere es jetzt. Alles Weitere baut darauf auf.
          </p>
        </div>

        {/* ── Open issues notice (pulled up for visibility) ── */}
        {missing_fields.length > 0 && (
          <div className="flex items-center gap-3 rounded-lg border border-amber-500/20 bg-amber-500/[0.03] px-5 py-3">
            <AlertCircle className="h-4 w-4 text-amber-600 shrink-0" />
            <div className="flex-1">
              <p className="text-[13px] text-foreground/70 font-medium">
                Noch offen: {missing_fields.length} {missing_fields.length === 1 ? 'Punkt muss' : 'Punkte müssen'} geklärt werden
              </p>
              <p className="text-[11px] text-muted-foreground/50 mt-0.5">
                {missing_fields.map(f => FIELD_LABELS[f] || f).join(', ')}
              </p>
            </div>
          </div>
        )}

        {/* Document sources */}
        {hasDocSources && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground/50 -mt-4">
            <FileText className="h-3 w-3 shrink-0" />
            <span>Ergänzt aus: {parsedFiles.map(f => f.fileName).join(', ')}</span>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════ */}
        {/* ── BLOCK 1: DILEMMA — THE HERO ──                        */}
        {/* ══════════════════════════════════════════════════════════ */}
        <div className={cn(
          'rounded-xl border-2 p-6 lg:p-8 transition-all duration-300',
          dilemmaConfirmed
            ? 'border-emerald-500/25 bg-emerald-500/[0.015]'
            : 'border-primary/30 bg-primary/[0.015]',
        )}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-[11px] font-semibold text-foreground/50 uppercase tracking-[0.15em]">
              Erkanntes Dilemma
            </h3>
            {dilemmaConfirmed && (
              <span className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5 font-medium">
                <Check className="h-3 w-3" /> Fixiert
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <ExtractionField
              label="Option A"
              field={fields.optionA || defaultField('', 0)}
              onUpdate={(v) => onUpdateField('optionA', v)}
              prominent
              multiline
            />
            <ExtractionField
              label="Option B"
              field={fields.optionB || defaultField('', 0)}
              onUpdate={(v) => onUpdateField('optionB', v)}
              prominent
              multiline
            />
          </div>

          {dilemmaConflicts.map((c, i) => <ConflictNotice key={i} conflict={c} />)}

          {/* Preference selector */}
          <div className="pt-5 mt-5 border-t border-border/20">
            <p className="text-xs text-foreground/50 mb-3">Wohin tendierst du aktuell?</p>
            <div className="flex items-center gap-2">
              {(['a', 'b', 'undecided'] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => onUpdatePreference(p)}
                  className={cn(
                    'px-4 py-2 rounded-lg text-xs font-medium transition-all duration-200',
                    preference === p
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground/60 hover:text-foreground hover:bg-muted/40 border border-border/30',
                  )}
                >
                  {p === 'a' ? 'Option A' : p === 'b' ? 'Option B' : 'Ich bin unsicher'}
                </button>
              ))}
            </div>
          </div>

          {/* Confirm / Fix CTA */}
          {!dilemmaConfirmed && (
            <div className="mt-6 flex items-center gap-3">
              <Button
                onClick={onConfirmDilemma}
                className="gap-1.5 font-medium"
              >
                <Check className="h-3.5 w-3.5" />
                Dilemma stimmt so
              </Button>
              <span className="text-[11px] text-muted-foreground/40">
                Du kannst oben editieren, falls etwas nicht passt.
              </span>
            </div>
          )}
        </div>

        {/* ══════════════════════════════════════════════════════════ */}
        {/* ── BLOCK 2: ZIEL & RAHMEN (collapsible) ──               */}
        {/* ══════════════════════════════════════════════════════════ */}
        {(fields.successDefinition || fields.timeHorizonDays) && (
          <CollapsibleSection title="Ziel & Rahmen" defaultOpen>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {fields.successDefinition && (
                <ExtractionField label="Ziel" field={fields.successDefinition} onUpdate={(v) => onUpdateField('successDefinition', v)} />
              )}
              {fields.timeHorizonDays && (
                <ExtractionField label="Zeitrahmen" field={{
                  ...fields.timeHorizonDays,
                  value: fields.timeHorizonDays.value ? `${fields.timeHorizonDays.value} Tage` : '—',
                }} onUpdate={(v) => onUpdateField('timeHorizonDays', v.replace(/[^\d]/g, ''))} />
              )}
            </div>
          </CollapsibleSection>
        )}

        {/* ══════════════════════════════════════════════════════════ */}
        {/* ── BLOCK 3: KRITISCHE ANNAHMEN (collapsible) ──          */}
        {/* ══════════════════════════════════════════════════════════ */}
        {derived.core_assumptions.length > 0 && (
          <CollapsibleSection
            title="Kritische Annahmen"
            badge={
              <span className="text-[10px] text-muted-foreground/40 font-mono">{derived.core_assumptions.length}</span>
            }
          >
            <div className="space-y-2">
              {derived.core_assumptions.map((a, i) => (
                <div key={i} className="flex items-start gap-3 text-sm text-foreground/80 rounded-lg bg-muted/10 px-4 py-3">
                  <span className="text-muted-foreground/30 text-xs mt-0.5 font-mono">{i + 1}</span>
                  <span className="leading-relaxed">{a}</span>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* ══════════════════════════════════════════════════════════ */}
        {/* ── BLOCK 4: RISIKEN (collapsible) ──                     */}
        {/* ══════════════════════════════════════════════════════════ */}
        {derived.key_risks.length > 0 && (
          <CollapsibleSection
            title="Risiken"
            badge={
              <span className="text-[10px] text-destructive/50 font-mono">{derived.key_risks.length}</span>
            }
          >
            <div className="space-y-2">
              {derived.key_risks.map((r, i) => (
                <div key={i} className="flex items-start gap-3 text-sm text-foreground/80 bg-destructive/[0.02] border border-destructive/8 rounded-lg px-4 py-3">
                  <AlertCircle className="h-3.5 w-3.5 text-destructive/40 mt-0.5 shrink-0" />
                  <span className="leading-relaxed">{r}</span>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* ══════════════════════════════════════════════════════════ */}
        {/* ── BLOCK 5: EINORDNUNG (collapsible) ──                  */}
        {/* ══════════════════════════════════════════════════════════ */}
        <CollapsibleSection title="Einordnung" defaultOpen={true}>
          <ExtractionField
            label="Session-Titel"
            field={fields.sessionTitle || defaultField(classification.suggested_title, 0)}
            onUpdate={(v) => onUpdateField('sessionTitle', v)}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ExtractionField
              label="Bereich"
              field={fields.decisionType || defaultField(classification.decision_type, 0)}
              onUpdate={(v) => onUpdateField('decisionType', v)}
            />
            <ExtractionField
              label="Subtyp"
              field={fields.decisionSubtype || defaultField(classification.decision_subtype, 0)}
              onUpdate={(v) => onUpdateField('decisionSubtype', v)}
            />
          </div>

          {classificationConflicts.map((c, i) => <ConflictNotice key={i} conflict={c} />)}

          {/* Impact / Urgency sliders */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 pt-2">
            {/* Impact */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-foreground/60">Wie groß ist die Auswirkung?</span>
                <span className="text-lg font-semibold text-foreground tabular-nums">{impactScore}<span className="text-xs text-muted-foreground/40 font-normal">/10</span></span>
              </div>
              <Slider
                value={[impactScore]}
                onValueChange={([v]) => onUpdateScore('impactScore', v)}
                onValueCommit={([v]) => onUpdateScore('impactScore', v)}
                min={1} max={10} step={1}
              />
              <div className="flex items-center justify-between text-[10px] text-muted-foreground/30">
                <span>Begrenzt</span>
                <span>Existenziell</span>
              </div>
              {fields.impactScore?.confirmation_status === 'unreviewed' && (
                <button
                  onClick={() => onAcceptScore ? onAcceptScore('impactScore') : onUpdateScore('impactScore', impactScore)}
                  className="text-[11px] text-primary/60 hover:text-primary transition-colors"
                >
                  Wert übernehmen →
                </button>
              )}
            </div>

            {/* Urgency */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-foreground/60">Wie dringend ist die Entscheidung?</span>
                <span className="text-lg font-semibold text-foreground tabular-nums">{urgencyScore}<span className="text-xs text-muted-foreground/40 font-normal">/10</span></span>
              </div>
              <Slider
                value={[urgencyScore]}
                onValueChange={([v]) => onUpdateScore('urgencyScore', v)}
                onValueCommit={([v]) => onUpdateScore('urgencyScore', v)}
                min={1} max={10} step={1}
              />
              <div className="flex items-center justify-between text-[10px] text-muted-foreground/30">
                <span>Kann warten</span>
                <span>Sofort</span>
              </div>
              {fields.urgencyScore?.confirmation_status === 'unreviewed' && (
                <button
                  onClick={() => onAcceptScore ? onAcceptScore('urgencyScore') : onUpdateScore('urgencyScore', urgencyScore)}
                  className="text-[11px] text-primary/60 hover:text-primary transition-colors"
                >
                  Wert übernehmen →
                </button>
              )}
            </div>
          </div>
        </CollapsibleSection>

        {/* ══════════════════════════════════════════════════════════ */}
        {/* ── FINAL CTA ──                                          */}
        {/* ══════════════════════════════════════════════════════════ */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 border-t border-border/30">
          <p className="text-xs text-muted-foreground/50 max-w-[55%] text-center sm:text-left">
            {canContinue
              ? 'Entscheidungskern fixiert. Starte die Analyse.'
              : !dilemmaConfirmed
                ? 'Fixiere zuerst den Entscheidungskern oben.'
                : `Noch ${readiness.unresolvedCriticalCount} offene${readiness.unresolvedCriticalCount > 1 ? ' Punkte' : 'r Punkt'}.`}
          </p>
          <Button
            onClick={onContinue}
            disabled={!canContinue}
            size="lg"
            className={cn(
              'gap-2 transition-all duration-300 font-medium px-8',
              canContinue && 'shadow-md shadow-primary/10 hover:shadow-lg hover:shadow-primary/20 hover:-translate-y-0.5',
            )}
          >
            {canContinue ? 'Weiter zur Analyse' : 'Offene Punkte klären'}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
